import React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '../../../lib/utils'
import { cardVariants } from '../../../utils/layoutVariants'

export interface CardProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  as?: React.ElementType
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { className, variant, padding, rounded, as: Component = 'div', ...props },
    ref
  ) => {
    return (
      <Component
        ref={ref}
        className={cn(cardVariants({ variant, padding, rounded, className }))}
        {...props}
      />
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { heroSpacing?: boolean }
>(({ className, heroSpacing = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col space-y-space-xs px-space-lg pt-5',
      heroSpacing ? 'pb-space-xl' : 'pb-space-md',
      className
    )}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    size?: 'sm' | 'md' | 'lg'
    display?: boolean
  }
>(({ className, size = 'md', display = false, ...props }, ref) => {
  const sizeClasses = {
    sm: 'text-fs-300 font-semibold',
    md: 'text-fs-400 font-semibold',
    lg: 'text-fs-600 font-semibold',
  }

  return (
    <h3
      ref={ref}
      className={cn(
        'font-serif leading-tight tracking-tight text-ink-700',
        sizeClasses[size as keyof typeof sizeClasses],
        display && 'text-fs-700',
        className
      )}
      {...props}
    />
  )
})
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & {
    size?: 'sm' | 'md' | 'lg'
    muted?: boolean
  }
>(({ className, size = 'sm', muted = false, ...props }, ref) => {
  const sizeClasses = {
    sm: 'text-fs-300',
    md: 'text-fs-400',
    lg: 'text-fs-400',
  }

  return (
    <p
      ref={ref}
      className={cn(
        'font-sans leading-relaxed',
        sizeClasses[size],
        muted ? 'text-ink-500' : 'text-ink-700',
        className
      )}
      {...props}
    />
  )
})
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { flush?: boolean }
>(({ className, flush = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'px-space-lg pb-space-lg',
      flush ? 'pt-0' : 'pt-space-sm',
      className
    )}
    {...props}
  />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    direction?: 'row' | 'column'
    justify?: 'start' | 'center' | 'between' | 'end'
  }
>(({ className, direction = 'row', justify = 'start', ...props }, ref) => {
  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    between: 'justify-between',
    end: 'justify-end',
  }

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-3 px-space-lg pb-4 pt-4',
        direction === 'column' ? 'flex-col' : 'flex-row',
        justifyClasses[justify],
        className
      )}
      {...props}
    />
  )
})
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
